import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { TransactionWithInstrument } from "@/types/Transaction";
import { HoldingWithInstrument } from "@/types/Holding";

interface UnconfirmedItemsSectionProps {
  unconfirmedTransactions: TransactionWithInstrument[];
  totalTransactions: number;
  unconfirmedHoldings: HoldingWithInstrument[];
  totalHoldings: number;
}

export const UnconfirmedItemsSection: React.FC<UnconfirmedItemsSectionProps> = ({
  unconfirmedTransactions,
  totalTransactions,
  unconfirmedHoldings,
  totalHoldings,
}) => {
  const navigate = useNavigate();

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Unconfirmed Transactions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Unconfirmed Transactions</CardTitle>
          <CardDescription>
            Transactions pending confirmation (
            {unconfirmedTransactions.length} of{" "}
            {totalTransactions})
          </CardDescription>
        </CardHeader>
        <CardContent>
          {unconfirmedTransactions.length > 0 ? (
            <div className="space-y-4">
              {unconfirmedTransactions
                .slice(0, 5)
                .map((transaction: TransactionWithInstrument) => (
                  <div
                    key={transaction.transactionId}
                    className="flex items-center justify-between border-b pb-2"
                  >
                    <div>
                      <p className="font-medium">
                        {transaction.instrument?.name || "Unknown Instrument"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(
                          transaction.transactionDate
                        ).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">
                        {transaction.currency}{" "}
                        {transaction.baseAmount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {transaction.transactionType}
                      </p>
                    </div>
                  </div>
                ))}
              {unconfirmedTransactions.length > 5 && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => navigate("/app/transactions")}
                  className="mt-2 p-0 h-auto"
                >
                  View all {unconfirmedTransactions.length}{" "}
                  transactions
                </Button>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground">
              No unconfirmed transactions
            </p>
          )}
        </CardContent>
      </Card>

      {/* Unconfirmed Holdings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Unconfirmed Holdings</CardTitle>
          <CardDescription>
            Holdings pending confirmation (
            {unconfirmedHoldings.length} of{" "}
            {totalHoldings})
          </CardDescription>
        </CardHeader>
        <CardContent>
          {unconfirmedHoldings.length > 0 ? (
            <div className="space-y-4">
              {unconfirmedHoldings
                .slice(0, 5)
                .map((holding: HoldingWithInstrument) => (
                  <div
                    key={holding.holdingId}
                    className="flex items-center justify-between border-b pb-2"
                  >
                    <div>
                      <p className="font-medium">
                        {holding.instrument?.name || "Unknown Instrument"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {holding.quantity.toLocaleString()} units
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">
                        {holding.currency}{" "}
                        {holding.baseAmount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              {unconfirmedHoldings.length > 5 && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => navigate("/app/holdings")}
                  className="mt-2 p-0 h-auto"
                >
                  View all {unconfirmedHoldings.length} holdings
                </Button>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground">No unconfirmed holdings</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}; 